import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Loader2, Users, ChevronRight, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBusinesses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/companies`);
      setItems(data.companies || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load businesses");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const expand = async (c) => {
    if (expanded === c.id) { setExpanded(null); setDetail(null); return; }
    setExpanded(c.id); setDetail(null);
    try {
      const { data } = await axios.get(`${API}/admin/companies/${c.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Detail load failed");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-businesses">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Businesses</div>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">EasyStay Business accounts</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Every registered company, their team size, pending booking requests, and total ZMW invoiced. Click a row to see members and paid-by-invoice bookings.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-shadow">
        <table className="w-full text-sm" data-testid="businesses-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Members</th>
              <th className="px-4 py-3">Pending</th>
              <th className="px-4 py-3 text-right">Invoiced (K)</th>
              <th className="px-4 py-3 text-right">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading businesses…
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">No business accounts yet.</td></tr>
            ) : items.map((c) => (
              <React.Fragment key={c.id}>
                <tr onClick={() => expand(c)} data-testid={`biz-row-${c.name}`} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-semibold text-slate-800">
                      <ChevronRight className={`h-3 w-3 text-slate-400 transition ${expanded === c.id ? "rotate-90" : ""}`} />
                      <Building2 className="h-4 w-4 text-copper" />
                      {c.name}
                    </div>
                    <div className="text-xs text-slate-500">{c.billing_email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <Users className="mr-1 inline h-3.5 w-3.5 text-slate-400" /> {c.member_count}
                  </td>
                  <td className="px-4 py-3">
                    {c.pending_requests > 0 ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{c.pending_requests}</span>
                    ) : (
                      <span className="text-xs text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-base font-bold text-slate-900">
                    K{Number(c.total_invoiced_zmw || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr data-testid={`biz-detail-${c.name}`}>
                    <td colSpan={5} className="bg-slate-50 px-4 py-4">
                      {!detail ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <div className="grid gap-6 lg:grid-cols-2">
                          <div>
                            <div className="mb-2 text-xs uppercase tracking-widest text-slate-500">Members ({detail.members.length})</div>
                            <div className="max-h-80 overflow-auto rounded-xl border border-slate-200 bg-white">
                              <table className="w-full text-sm">
                                <tbody className="divide-y divide-slate-100">
                                  {detail.members.map((m) => (
                                    <tr key={m.user_id}>
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800">{m.name || "—"}</div>
                                        <div className="text-xs text-slate-500">{m.email}</div>
                                      </td>
                                      <td className="px-3 py-2 text-xs capitalize text-slate-500">{m.role}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div>
                            <div className="mb-2 text-xs uppercase tracking-widest text-slate-500">Invoiced bookings ({detail.bookings.length})</div>
                            <div className="max-h-80 overflow-auto rounded-xl border border-slate-200 bg-white">
                              <table className="w-full text-sm">
                                <tbody className="divide-y divide-slate-100">
                                  {detail.bookings.length === 0 ? (
                                    <tr><td className="px-3 py-4 text-center text-xs text-slate-400">No invoiced bookings yet</td></tr>
                                  ) : detail.bookings.map((b) => (
                                    <tr key={b.id}>
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800">{b.property_title}</div>
                                        <div className="text-xs text-slate-500">{new Date(b.check_in).toLocaleString()} · {b.guest_name}</div>
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        <div className="font-display font-bold">K{Number(b.total_zmw).toFixed(2)}</div>
                                        <div className="text-[11px] text-slate-500 capitalize">{b.payment_status}</div>
                                        <a
                                          href={`${API}/bookings/${b.id}/invoice.pdf`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          data-testid={`admin-download-invoice-${b.id}`}
                                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-copper hover:underline"
                                        >
                                          <Download className="h-3 w-3" /> Invoice PDF
                                        </a>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
